import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../Coaching/NewPlayer.css';
import { useUser } from '../context/UserContext';
import axios from "axios";
import { fireAudit } from '../utils/auditClient';

const NewPlayer = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const { selectedCenter } = useUser();
  const [playerData, setPlayerData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('auth-token');
  const baselineRef = useRef(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setPlayerData([]);

    const searchUrl = `${process.env.REACT_APP_API_PLAYER_SEARCH}?academyId=${selectedCenter}&mobileNumber=${mobileNumber}`;

    try {
      const response = await fetch(
        searchUrl,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await response.json();

      if (data.success && data.data.searchData.length > 0) {
        setPlayerData(data.data.searchData);
      } else {
        setError('User not found');
      }

      const newPayload = {
        academyId: Number(selectedCenter),
        mobileNumber,
        success: !!data.success,
        resultCount: data?.data?.searchData?.length ?? 0
      };
      const oldPayload = baselineRef.current || {};
      fireAudit({
        entity: 'player_search',
        affected_id: `${selectedCenter}-${mobileNumber}`,
        action: 'search',
        section: 'Users',
        subSection: 'NewPlayer',
        businessApi: searchUrl,
        businessMethod: 'GET',
        oldPayload,
        newPayload,
        extra: { page: 'NewPlayer' },
      });
      baselineRef.current = newPayload;
    } catch (err) {
      setError('Error fetching user data');
      const newPayload = {
        academyId: Number(selectedCenter),
        mobileNumber,
        success: false,
        error: 'fetch_failed'
      };
      const oldPayload = baselineRef.current || {};
      fireAudit({
        entity: 'player_search',
        affected_id: `${selectedCenter}-${mobileNumber}`,
        action: 'search',
        section: 'Users',
        subSection: 'NewPlayer',
        businessApi: searchUrl,
        businessMethod: 'GET',
        oldPayload,
        newPayload,
        extra: { page: 'NewPlayer' },
      });
    }

    setLoading(false);
  };

  const handleSelectPlayer = async (player) => {
    const apiUrl = process.env.REACT_APP_API_PLAYER_GUARDIAN;

    try {
      const response = await axios.get(apiUrl, {
        params: {
          academy_id: selectedCenter,
          user_id: player.id,
          type: "PLAYER",
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      fireAudit({
        entity: 'player_selection',
        affected_id: String(player.id),
        action: 'select',
        section: 'Coaching',
        subSection: 'New Player',
        businessApi: `${apiUrl}?academy_id=${selectedCenter}&user_id=${player.id}&type=PLAYER`,
        businessMethod: 'GET',
        oldPayload: baselineRef.current || {},
        newPayload: {
          academyId: Number(selectedCenter),
          playerId: player.id,
          playerName: player.name || '',
          mobileNumberQueried: mobileNumber
        },
        extra: { page: 'NewPlayer' },
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Player set successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      navigate(-1);

    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to fetch player data.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleSetGuardian = (event, player) => {
    const url = `/setGuardian?userId=${player.id}&name=${encodeURIComponent(player.name)}`;
    fireAudit({
      entity: 'guardian_navigation',
      affected_id: String(player.id),
      affected_user: player.id,
      action: 'navigate',
      section: 'Users',
      subSection: 'NewPlayer',
      businessApi: url,
      businessMethod: 'GET',
      oldPayload: baselineRef.current || {},
      newPayload: {
        academyId: Number(selectedCenter),
        targetRoute: url,
        playerId: player.id,
        playerName: player.name || ''
      },
      extra: { page: 'NewPlayer' },
    });

    if (event?.metaKey || event?.ctrlKey) {
      window.open(url, "_blank"); 
    } else {
      navigate(url);
    }
  };

  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/" className="breadcrumb-link">
          Dashboard
        </Link> &gt; Search Player
      </div>

      <form onSubmit={handleSearch} className="new-player-form">
        <div className="floating-select-form" >
          <input
            type="text"
            id="mobileNumber"
            value={mobileNumber}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/\D/g, '');
              setMobileNumber(onlyNumbers);
            }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            maxLength={10}
            placeholder="Enter mobile number"
            required
            className={`walkin-form ${mobileNumber ? "has-value" : ""}`}
          />
          {mobileNumber && <label htmlFor="mobileNumber">Mobile Number</label>}
        </div>
        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {playerData.length > 0 ? (
        <div className="player-details">
          <h3>Players Found:</h3>
          <div style={{ overflow: "scroll" }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {playerData
                  .filter((player) => {
                    const cleanedMobile = player.mobile_number?.replace(/\D/g, '').slice(-10);
                    return cleanedMobile === mobileNumber;
                  })
                  .map((player) => (
                    <tr key={player.id}>
                      <td>{player.name || 'N/A'}</td>
                      <td>{player.mobile_number}</td>
                      <td>
                        <button
                          className="resolve-button"
                          onClick={(e) => handleSetGuardian(e, player)}
                        >
                          Set as Guardian
                        </button>
                        {player.user_type !== "FAMILY" && (
                          <button
                            className="resolved-button"
                            onClick={() => handleSelectPlayer(player)}
                          >
                            Select as Player
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>



            </table>
          </div>
        </div>
      ) : (
        <div className="no-player">
          {error && <p className="error-message">{error}</p>}
          {playerData.length === 0 && error && (
            <Link to="/createplayer" className="create-new-player-button">
              Create New Player
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NewPlayer;
